import { useState } from "react";
import { Link } from "react-router-dom";
import {
  MapPin, Calendar, Users, HandHeart, ArrowRight, BrickWall, Armchair, Droplets, PaintRoller, Waves,
  HardHat, School, ClipboardList, UserCheck,
} from "lucide-react";

export const STORIES = [
  {
    id: "mandya-visit",
    date: "September 2026",
    chip: "Recent News",
    title: <>Work begins at Hallagere; team visits <em>Besagarahalli &amp; Bidarakote.</em></>,
    listTitle: "Field visit to Besagarahalli & Bidarakote schools",
    location: "Hallagere · Besagarahalli · Bidarakote — Mandya region, Karnataka",
    body: (
      <>
        Work has officially started at Parinitha Aided Higher Primary School, Hallagere. In the same stretch, the
        Akshara Daana committee &mdash; under the leadership of <strong>Smt. Anitha Dasappa</strong> and{" "}
        <strong>Prof. Abhinandan</strong> &mdash; visited the schools at Besagarahalli and Bidarakote, analysed the
        situation on the ground, and reported to Smt. Revathi Kamath on the state of each school and the
        infrastructure development that is much needed.
      </>
    ),
    itemsTitle: "Highlights",
    itemsTone: "teal",
    items: [
      { icon: HardHat, text: "Construction and restoration work commenced at Hallagere school." },
      { icon: School, text: "Besagarahalli and Bidarakote schools inspected classroom by classroom." },
      { icon: ClipboardList, text: "Committee report on infrastructure needs submitted to Smt. Revathi Kamath." },
      { icon: UserCheck, text: "Students and teachers interacted with the team during the visit." },
    ],
    team: ["Smt. Anitha Dasappa — Lead", "Prof. Abhinandan — Lead", "Mrs. Sahana — In-house Coordinator, Akshara Daana Mandya"],
    teamTitle: "The committee",
    locals: null,
    cta: { label: "Fund the Hallagere works", to: "/get-involved#donate" },
    photos: [
      { src: "/images/mandya/team.jpg", alt: "The Akshara Daana committee with students during the school visit", caption: "The committee with students during the visit" },
      { src: "/images/mandya/classroom.jpg", alt: "A class in session during the Akshara Daana visit", caption: "A class in session during the visit" },
    ],
  },
  {
    id: "lakshmeshwar-visit",
    date: "31 Aug 2026",
    chip: "Field Report",
    title: <>Akshara Daana visits <em>Lakshmeshwar, Gadag.</em></>,
    listTitle: "Field visit to Lakshmeshwar, Gadag — team inspects Government Model Primary School and meets students",
    location: "Government Model Primary School, Lakshmeshwar — Gadag District, Karnataka",
    body: (
      <>
        On 31 August 2026 the Akshara Daana team travelled to Lakshmeshwar in Gadag district to see the school for
        ourselves. We walked every classroom, monitored the facilities, and sat down with the students and staff to
        listen. The children were bright, curious and full of hope &mdash; and keen that their school be taken up for
        development by Akshara Daana.
      </>
    ),
    itemsTitle: "What we observed",
    itemsTone: "coral",
    items: [
      { icon: BrickWall, text: "The compound wall has collapsed, leaving the campus unprotected." },
      { icon: Armchair, text: "Classrooms have no benches — children sit on the floor." },
      { icon: Droplets, text: "No proper toilets for students or staff." },
      { icon: PaintRoller, text: "Walls have not been painted in years." },
      { icon: Waves, text: "No handwashing units on the premises." },
    ],
    team: ["Smt. Revathi Kamath", "Smt. Anitha Dasappa", "Shri Manu C P", "Dr. Abhinandan"],
    teamTitle: "The team on the ground",
    locals: [
      { name: "Shri Poojar", role: "Senior Journalist" },
      { name: "Shri Girish Agadi", role: "Businessman" },
      { name: "Shri Doddamani", role: "Principal of the school" },
    ],
    cta: { label: "Help us take up this school", to: "/get-involved#donate" },
    photos: [
      { src: "/images/lakshmeshwar/group.jpg", alt: "Smt. Revathi Kamath and Smt. Anitha Dasappa with students of the Lakshmeshwar school", caption: "Students with Smt. Revathi Kamath & Smt. Anitha Dasappa" },
      { src: "/images/lakshmeshwar/classroom.jpg", alt: "Inside a classroom at the Government Model Primary School, Lakshmeshwar", caption: "Inside the classroom — no benches for the children" },
      { src: "/images/lakshmeshwar/courtyard.jpg", alt: "The Akshara Daana team with local supporters inspecting the school grounds", caption: "Inspecting the grounds with local supporters" },
    ],
  },
];

function PhotoTile({ p, id, i, onOpen, className = "" }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(p)}
      data-testid={`${id}-photo-${i}`}
      className={`group relative overflow-hidden bg-navy/10 w-full ${className}`}
    >
      <img src={p.src} alt={p.alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
      <span className="absolute bottom-2 left-2 right-2 text-left bg-navy/80 text-white text-[10px] sm:text-xs font-display font-semibold px-2.5 py-1 rounded-sm leading-snug">
        {p.caption}
      </span>
    </button>
  );
}

const TONE = {
  coral: { label: "text-coral", bubble: "bg-coral/10 text-coral", chip: "section-chip section-chip-coral" },
  teal: { label: "text-teal", bubble: "bg-teal/10 text-teal", chip: "section-chip" },
};

export function NewsStory({ story }) {
  const [active, setActive] = useState(null);
  const tone = TONE[story.itemsTone];
  const rest = story.photos.slice(1);

  return (
    <article id={story.id} data-testid={`news-story-${story.id}`} className="manuscript-card !p-0 overflow-hidden scroll-mt-32">
      <div className="grid lg:grid-cols-12">
        <div className="lg:col-span-6 flex flex-col gap-1.5 p-1.5 bg-navy/5">
          <PhotoTile p={story.photos[0]} id={story.id} i={0} onOpen={setActive} className="h-[220px] sm:h-[320px] lg:h-auto lg:flex-1 lg:min-h-[260px]" />
          {rest.length > 0 && (
            <div className="grid grid-cols-2 gap-1.5">
              {rest.map((p, i) => (
                <PhotoTile key={p.src} p={p} id={story.id} i={i + 1} onOpen={setActive} className={rest.length === 1 ? "col-span-2 aspect-[16/9] sm:aspect-[16/7]" : "aspect-[4/3]"} />
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-6 p-6 sm:p-8 lg:p-10 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={tone.chip}>{story.chip}</span>
            <span className="inline-flex items-center gap-1.5 text-navy/60 font-display text-xs font-bold uppercase tracking-widest">
              <Calendar className="w-3.5 h-3.5 text-teal" /> {story.date}
            </span>
          </div>
          <h3 className="mt-4 font-display font-extrabold text-navy text-2xl sm:text-3xl md:text-4xl leading-[1.1] tracking-tight">
            {story.title}
          </h3>
          <div className="mt-2 flex items-start gap-2 text-navy/70 font-display text-sm">
            <MapPin className="w-4 h-4 text-teal mt-0.5 shrink-0" />
            <span>{story.location}</span>
          </div>

          <p className="mt-5 font-display text-navy/80 text-base leading-relaxed">{story.body}</p>

          <div className="mt-6">
            <div className={`text-[10px] uppercase tracking-widest font-display font-bold ${tone.label}`}>{story.itemsTitle}</div>
            <ul className="mt-3 space-y-2.5">
              {story.items.map((o) => {
                const Icon = o.icon;
                return (
                  <li key={o.text} className="flex items-start gap-3 font-display text-navy/80 text-sm leading-snug">
                    <span className={`mt-0.5 w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${tone.bubble}`}>
                      <Icon className="w-3.5 h-3.5" />
                    </span>
                    {o.text}
                  </li>
                );
              })}
            </ul>
          </div>

          <div className={`mt-6 grid gap-5 ${story.locals ? "sm:grid-cols-2" : ""}`}>
            <div>
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-display font-bold text-teal">
                <Users className="w-3.5 h-3.5" /> {story.teamTitle}
              </div>
              <ul className="mt-2 space-y-1 font-display text-navy/80 text-sm">
                {story.team.map((t) => <li key={t}>{t}</li>)}
              </ul>
            </div>
            {story.locals && (
              <div>
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-display font-bold text-teal">
                  <HandHeart className="w-3.5 h-3.5" /> Local support
                </div>
                <ul className="mt-2 space-y-1 font-display text-navy/80 text-sm">
                  {story.locals.map((l) => (
                    <li key={l.name}>{l.name} <span className="text-navy/50">&middot; {l.role}</span></li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <Link to={story.cta.to} data-testid={`${story.id}-cta`} className="mt-7 inline-flex items-center gap-2 btn-vermilion">
            {story.cta.label}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {active && (
        <div
          onClick={() => setActive(null)}
          data-testid={`${story.id}-lightbox`}
          className="fixed inset-0 bg-navy/85 z-[100] flex items-center justify-center p-4 cursor-zoom-out"
        >
          <img src={active.src} alt={active.alt} className="max-h-[92vh] max-w-[92vw] object-contain shadow-2xl" />
        </div>
      )}
    </article>
  );
}

export default function FieldVisitNews() {
  return (
    <div className="space-y-8 md:space-y-10">
      {STORIES.map((s) => <NewsStory key={s.id} story={s} />)}
    </div>
  );
}
